import React, { useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Add, DeleteOutline, Edit } from "@material-ui/icons";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../ConfirmationModal";
import ModalImageCors from "../ModalImageCors";
import LocationPreview from "../LocationPreview";
import { AuthContext } from "../../context/Auth/AuthContext";
import { usePrivacyCensorSettings } from "../../utils/privacyCensor";

const useStyles = makeStyles(theme => ({
  headerActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  formBox: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    border: "1px solid rgba(0,0,0,.12)",
    borderRadius: 6,
    background: "#fafafa",
  },
  orderRow: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
  },
  rowHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: theme.spacing(1),
  },
  rowActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  orderLegacyHeader: {
    "& > :not(:first-child)": {
      display: "none",
    },
  },
  rowTitle: {
    fontWeight: 600,
  },
  muted: {
    color: theme.palette.text.secondary,
  },
  orderValues: {
    marginTop: theme.spacing(1),
  },
  orderValueItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(0.75, 0),
  },
  orderFileCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  orderFileActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  orderMediaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  orderMediaPanel: {
    minWidth: 0,
  },
  orderLocationBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    minHeight: 178,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& > div": {
      minWidth: "0 !important",
      maxWidth: "100% !important",
      width: "100%",
      padding: "0 !important",
    },
    "& iframe, & img": {
      height: "86px !important",
    },
  },
  orderProofBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1),
    minHeight: 178,
    display: "grid",
    gap: theme.spacing(1),
  },
  orderProofPreviewBox: {
    minHeight: 92,
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(0.75),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  orderProofPreview: {
    width: "100%",
    height: 88,
    objectFit: "contain",
    cursor: "pointer",
  },
  orderProofPdfPreview: {
    border: 0,
    width: "100%",
    height: 88,
  },
  itemRow: {
    display: "grid",
    gridTemplateColumns: "auto minmax(260px, 2fr) repeat(3, minmax(96px, 1fr))",
    gap: theme.spacing(1),
    alignItems: "flex-start",
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      padding: theme.spacing(1.5),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      backgroundColor: theme.palette.background.paper,
    },
  },
  itemCatalogField: {
    minWidth: 0,
  },
  itemCatalogWideField: {
    minWidth: 0,
  },
  itemProductField: {
    flex: "1 1 190px",
    minWidth: 0,
  },
  itemVariantField: {
    flex: "1 1 190px",
    minWidth: 0,
  },
  itemCatalogVariantField: {
    flex: "2 1 240px",
    minWidth: 0,
  },
  itemQuantityField: {
    minWidth: 0,
    width: "100%",
  },
  itemPriceField: {
    minWidth: 0,
    width: "100%",
  },
  itemDeleteButton: {
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      justifySelf: "flex-end",
    },
  },
  itemOptionsGrid: {
    gridColumn: "2 / -1",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 6,
    backgroundColor: theme.palette.action.hover,
    [theme.breakpoints.down("sm")]: {
      gridColumn: "auto",
    },
  },
  itemOptionControl: {
    minWidth: 0,
    width: "100%",
  },
  itemOptionPrice: {
    marginTop: theme.spacing(0.25),
    minHeight: 18,
  },
  itemTotalBox: {
    marginTop: theme.spacing(1),
    minWidth: 0,
    width: "100%",
  },
  checkoutRow: {
    display: "grid",
    gridTemplateColumns: "160px minmax(240px, 1fr) auto",
    gap: theme.spacing(1),
    alignItems: "flex-start",
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  emptyText: {
    color: theme.palette.text.secondary,
  },
  proofPreview: {
    width: "100%",
    height: 140,
    cursor: "pointer",
    objectFit: "cover",
  },
  proofPdfPreview: {
    border: 0,
    width: "100%",
    height: 140,
  },
  proofBox: {
    padding: theme.spacing(1),
    border: "1px solid rgba(0,0,0,.12)",
    borderRadius: 6,
    display: "grid",
    gap: theme.spacing(1),
    height: "100%",
  },
  proofGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    alignItems: "stretch",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  proofPanel: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  locationBox: {
    padding: theme.spacing(1),
    border: "1px solid rgba(0,0,0,.12)",
    borderRadius: 6,
    flex: 1,
    minHeight: 0,
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    "& > div": {
      minWidth: "0 !important",
      maxWidth: "100% !important",
      width: "100%",
      padding: "0 !important",
    },
  },
  proofContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  proofName: {
    display: "block",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  proofActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  proofPreviewBox: {
    minHeight: 142,
    border: "1px dashed rgba(0,0,0,.12)",
    borderRadius: 6,
    padding: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  paymentMethodActions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    alignItems: "center",
    marginTop: theme.spacing(0.5),
  },
  censoredText: {
    filter: "blur(4px)",
    userSelect: "none",
  },
}));

const paymentStatuses = {
  pending: "Pendiente",
  proof_received: "Comprobante recibido",
  reviewing: "En revision",
  paid: "Pagado",
  rejected: "Rechazado",
  refunded: "Reembolsado",
};

const NO_PAYMENT_METHOD = "no-payment-method";
const NO_SHIPPING_METHOD = "no-shipping-method";
const NO_CATALOG_PRODUCT = "no-catalog-product";
const NO_PRODUCT_VARIANT = "no-product-variant";

const toOptionalValue = (value, emptyValue) => (value === emptyValue ? "" : value || "");

const emptyOrderItem = {
  catalogProductId: NO_CATALOG_PRODUCT,
  productName: "",
  catalogOptions: [],
  optionSelections: {},
  basePrice: 0,
  variantName: NO_PRODUCT_VARIANT,
  quantity: 1,
  unitPrice: 0,
  notes: "",
};

const emptyMethod = {
  id: null,
  name: "",
  description: "",
  instructions: "",
  requiresProof: false,
  isActive: true,
};

const emptyShippingMethod = {
  id: null,
  name: "",
  description: "",
  cost: 0,
  requiresLocation: false,
  isActive: true,
};

const emptyForm = {
  id: null,
  status: "new",
  paymentStatus: "pending",
  paymentMethodId: NO_PAYMENT_METHOD,
  shippingMethodId: NO_SHIPPING_METHOD,
  discount: 0,
  tax: 0,
  shipping: 0,
  customerNotes: "",
  internalNotes: "",
  checkoutFields: [],
  items: [emptyOrderItem],
};

const defaultCheckoutFields = [
  { name: "Nombre", value: "" },
  { name: "Teléfono", value: "" },
  { name: "Dirección exacta", value: "" },
  { name: "Referencias", value: "" },
];

const normalizeFieldName = value =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const isDeliveryLocationField = field =>
  normalizeFieldName(field?.name) === "ubicacion de entrega";

const isPdfFile = ({ mimeType = "", name = "", url = "" } = {}) =>
  /^application\/pdf/i.test(mimeType || "") || /\.pdf($|\?)/i.test(name || url || "");

const isImageFile = ({ mimeType = "", name = "", url = "" } = {}) =>
  /^image\//i.test(mimeType || "") || /\.(png|jpe?g|gif|webp|bmp)$/i.test(name || url || "");

const buildFileHref = url => {
  if (!url) return "";
  if (/^(https?:|blob:)/i.test(url)) return url;
  return `${String(api.defaults.baseURL || "").replace(/\/+$/, "")}/${String(url).replace(/^\/+/, "")}`;
};

const emptySettings = {
  currencySymbol: "$",
  decimalPlaces: 2,
  checkoutFields: defaultCheckoutFields,
};

const checkoutFieldTypes = [
  "text",
  "number",
  "date",
  "email",
  "phone",
  "textarea",
  "select",
  "multiple",
];

const conditionOperators = ["equals", "not_equals", "contains"];

const normalizeCheckoutConditionalPayload = config => ({
  enabled: config?.enabled === true,
  sourceFieldName: String(config?.sourceFieldName || "").trim(),
  operator: conditionOperators.includes(config?.operator) ? config.operator : "equals",
  value: String(config?.value || "").trim(),
});

const normalizeCheckoutFieldOptions = options =>
  (Array.isArray(options) ? options : [])
    .map(option => (typeof option === "string" ? option : option?.label || option?.name || ""))
    .map(option => String(option || "").trim())
    .filter(Boolean);

const normalizeCheckoutFieldType = type =>
  checkoutFieldTypes.includes(type) ? type : "text";

const money = (value, settings = emptySettings) =>
  `${settings.currencySymbol || ""}${Number(value || 0).toLocaleString("es-CR", {
    minimumFractionDigits: Number(settings.decimalPlaces || 0),
    maximumFractionDigits: Number(settings.decimalPlaces || 0),
  })}`;

const normalizeCheckoutFields = fields =>
  (fields?.length ? fields : defaultCheckoutFields).map(field => ({
    name: field.name || "",
    value: field.value || "",
    type: normalizeCheckoutFieldType(field.type || "text"),
    required: field.required !== false,
    options: normalizeCheckoutFieldOptions(field.options),
    conditionalConfig: normalizeCheckoutConditionalPayload(field.conditionalConfig),
  }));

const buildCheckoutFieldsFromSettings = settings =>
  normalizeCheckoutFields(settings?.checkoutFields).map(field => ({ ...field }));

const normalizeItems = items =>
  (items || []).map(item => ({
    catalogProductId: item.catalogProductId || item.productId || item.product?.id || NO_CATALOG_PRODUCT,
    productName: item.productName || item.product?.name || "",
    catalogOptions: item.catalogOptions || item.product?.options || [],
    optionSelections: item.optionSelections || {},
    basePrice: Number(item.basePrice || item.product?.price || item.unitPrice || 0),
    variantName: item.variantName || NO_PRODUCT_VARIANT,
    quantity: Number(item.quantity || 1),
    unitPrice: Number(item.unitPrice || 0),
    notes: item.notes || "",
  }));

const sanitizeItems = items =>
  (items || []).map(item => ({
    productName: item.productName,
    sku: item.sku || "",
    quantity: item.quantity,
    unitPrice: item.unitPrice,
    notes: item.notes || "",
    productId: toOptionalValue(item.catalogProductId, NO_CATALOG_PRODUCT),
    catalogProductId: toOptionalValue(item.catalogProductId, NO_CATALOG_PRODUCT),
    optionSelections: item.optionSelections || {},
    variantName: toOptionalValue(
      buildItemOptionText(item) || item.variantName,
      NO_PRODUCT_VARIANT
    ),
  }));

const normalizeSelectionArray = value => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const getOptionValues = option => option?.values || [];

const getSelectedOptionValues = (option, selection) => {
  const selectedNames = normalizeSelectionArray(selection);
  return getOptionValues(option).filter(value => selectedNames.includes(value.name));
};

const getOptionExtra = (option, selection) =>
  getSelectedOptionValues(option, selection).reduce(
    (sum, value) => sum + Number(value.priceDelta || 0),
    0
  );

const calculateItemUnitPrice = item =>
  Number(item.basePrice || 0) +
  (item.catalogOptions || []).reduce(
    (sum, option) => sum + getOptionExtra(option, item.optionSelections?.[option.name]),
    0
  );

const buildItemOptionText = item =>
  (item.catalogOptions || [])
    .map(option => {
      const selected = item.optionSelections?.[option.name];
      const selectedValues = normalizeSelectionArray(selected).filter(Boolean);
      if (!selectedValues.length) return "";
      return `${option.name}: ${selectedValues.join(", ")}`;
    })
    .filter(Boolean)
    .join(" | ");

const TicketOrdersModal = ({ modalOpen, onClose, ticket }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const privacyCensorSettings = usePrivacyCensorSettings(user?.id);
  const isMounted = useRef(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [shippingMethods, setShippingMethods] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [orderSettings, setOrderSettings] = useState(emptySettings);
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [deletingOrder, setDeletingOrder] = useState(null);
  const [methodModalOpen, setMethodModalOpen] = useState(false);
  const [methodForm, setMethodForm] = useState(emptyMethod);
  const [shippingMethodModalOpen, setShippingMethodModalOpen] = useState(false);
  const [shippingMethodForm, setShippingMethodForm] = useState(emptyShippingMethod);
  const [proofFile, setProofFile] = useState(null);
  const [proofFilePreviewUrl, setProofFilePreviewUrl] = useState("");
  const [proofRemoved, setProofRemoved] = useState(false);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!proofFile) {
      setProofFilePreviewUrl("");
      return undefined;
    }

    const nextUrl = window.URL.createObjectURL(proofFile);
    setProofFilePreviewUrl(nextUrl);
    return () => window.URL.revokeObjectURL(nextUrl);
  }, [proofFile]);

  const resetForm = () => {
    setProofFile(null);
    setProofRemoved(false);
    setForm({
      ...emptyForm,
      status: statuses[0]?.key || "new",
      items: [{ ...emptyOrderItem }],
      checkoutFields: buildCheckoutFieldsFromSettings(orderSettings),
      shippingMethodId: NO_SHIPPING_METHOD,
    });
  };

  const fetchData = async () => {
    if (!modalOpen || !ticket?.id) return;

    setLoading(true);
    try {
      const [
        { data: orderData },
        { data: productData },
        { data: methodData },
        { data: shippingMethodData },
        { data: statusData },
        { data: settingsData },
      ] =
        await Promise.all([
          api.get("/orders", { params: { ticketId: ticket.id } }),
          api.get("/orders/products", { params: { connectionId: ticket.connectionId } }),
          api.get("/orders/payment-methods", { params: { connectionId: ticket.connectionId } }),
          api.get("/orders/shipping-methods", { params: { connectionId: ticket.connectionId } }),
          api.get("/orders/statuses"),
          api.get("/orders/settings", { params: { connectionId: ticket.connectionId } }),
        ]);

      if (isMounted.current) {
        setOrders(orderData.orders || []);
        setProducts(productData.products || []);
        setPaymentMethods(methodData.paymentMethods || []);
        setShippingMethods(shippingMethodData.shippingMethods || []);
        setStatuses(statusData.statuses || []);
        setOrderSettings(settingsData.settings || emptySettings);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (modalOpen) {
      setShowForm(false);
      fetchData();
    }
  }, [modalOpen, ticket]);

  const orderSubtotal = (form.items || []).reduce(
    (sum, item) => sum + Number(item.quantity || 0) * Number(item.unitPrice || 0),
    0
  );
  const orderTotal =
    orderSubtotal -
    Number(form.discount || 0) +
    Number(form.tax || 0) +
    Number(form.shipping || 0);

  const handleEdit = order => {
    setProofFile(null);
    setProofRemoved(false);
    setForm({
      ...emptyForm,
      ...order,
      paymentMethodId: order.paymentMethodId || NO_PAYMENT_METHOD,
      shippingMethodId: order.shippingMethodId || NO_SHIPPING_METHOD,
      items: normalizeItems(order.items),
      checkoutFields: order.checkoutFields || [],
    });
    setShowForm(true);
  };

  const addItem = () => {
    setForm(prev => ({
      ...prev,
      items: [
        ...(prev.items || []),
        { ...emptyOrderItem },
      ],
    }));
  };

  const addCheckoutField = () => {
    setForm(prev => ({
      ...prev,
      checkoutFields: [...(prev.checkoutFields || []), { name: "", value: "", type: "text", required: false }],
    }));
  };

  const updateItem = (index, patch) => {
    const items = [...(form.items || [])];
    items[index] = { ...items[index], ...patch };
    setForm({ ...form, items });
  };

  const closeMethodModal = () => {
    setMethodModalOpen(false);
    setMethodForm(emptyMethod);
  };

  const closeShippingMethodModal = () => {
    setShippingMethodModalOpen(false);
    setShippingMethodForm(emptyShippingMethod);
  };

  const openCreatePaymentMethod = () => {
    setMethodForm({ ...emptyMethod, connectionId: ticket.connectionId });
    setMethodModalOpen(true);
  };

  const openEditPaymentMethod = methodId => {
    if (!methodId || methodId === NO_PAYMENT_METHOD) return;
    const method = paymentMethods.find(entry => String(entry.id) === String(methodId));
    if (!method) return;
    setMethodForm({ ...emptyMethod, ...method });
    setMethodModalOpen(true);
  };

  const openCreateShippingMethod = () => {
    setShippingMethodForm({ ...emptyShippingMethod, connectionId: ticket.connectionId });
    setShippingMethodModalOpen(true);
  };

  const openEditShippingMethod = methodId => {
    if (!methodId || methodId === NO_SHIPPING_METHOD) return;
    const method = shippingMethods.find(entry => String(entry.id) === String(methodId));
    if (!method) return;
    setShippingMethodForm({ ...emptyShippingMethod, ...method });
    setShippingMethodModalOpen(true);
  };

  const savePaymentMethod = async () => {
    try {
      const payload = {
        ...methodForm,
        connectionId: methodForm.connectionId || ticket.connectionId,
      };
      const { data: savedMethod } = methodForm.id
        ? await api.put(`/orders/payment-methods/${methodForm.id}`, payload)
        : await api.post("/orders/payment-methods", payload);

      toast.success("Metodo de pago guardado");
      const { data } = await api.get("/orders/payment-methods", {
        params: { connectionId: ticket.connectionId },
      });
      setPaymentMethods(data.paymentMethods || []);
      if (savedMethod?.id) {
        setForm(prevState => ({
          ...prevState,
          paymentMethodId: savedMethod.id,
        }));
      }
      closeMethodModal();
    } catch (err) {
      toastError(err);
    }
  };

  const saveShippingMethod = async () => {
    try {
      const payload = {
        ...shippingMethodForm,
        connectionId: shippingMethodForm.connectionId || ticket.connectionId,
        cost: Number(shippingMethodForm.cost || 0),
      };
      const { data: savedMethod } = shippingMethodForm.id
        ? await api.put(`/orders/shipping-methods/${shippingMethodForm.id}`, payload)
        : await api.post("/orders/shipping-methods", payload);

      toast.success("Metodo de envio guardado");
      const { data } = await api.get("/orders/shipping-methods", {
        params: { connectionId: ticket.connectionId },
      });
      setShippingMethods(data.shippingMethods || []);
      if (savedMethod?.id) {
        setForm(prevState => ({
          ...prevState,
          shippingMethodId: savedMethod.id,
          shipping: Number(savedMethod.cost || 0),
        }));
      }
      closeShippingMethodModal();
    } catch (err) {
      toastError(err);
    }
  };

  const handleSave = async () => {
    if (!(form.items || []).some(item => String(item.productName || "").trim())) {
      toast.error("Agrega al menos una linea de producto.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        contactId: ticket.contactId || ticket.contact?.id,
        ticketId: ticket.id,
        connectionId: ticket.connectionId,
        userId: ticket.userId || "",
        customerName: ticket.contact?.name || "",
        customerPhone: ticket.contact?.number || "",
        customerEmail: ticket.contact?.email || "",
        paymentMethodId: toOptionalValue(form.paymentMethodId, NO_PAYMENT_METHOD),
        shippingMethodId: toOptionalValue(form.shippingMethodId, NO_SHIPPING_METHOD),
        discount: Number(form.discount || 0),
        tax: Number(form.tax || 0),
        shipping: Number(form.shipping || 0),
        items: sanitizeItems(form.items),
        clearPaymentProof: proofRemoved,
        source: "manual",
      };

      let savedOrder;
      if (form.id) {
        const { data } = await api.put(`/orders/${form.id}`, payload);
        savedOrder = data;
      } else {
        const { data } = await api.post("/orders", payload);
        savedOrder = data;
      }

      if (proofFile && savedOrder?.id) {
        const formData = new FormData();
        formData.append("paymentProof", proofFile);
        await api.post(`/orders/${savedOrder.id}/payment-proof`, formData);
      }

      toast.success("Pedido guardado");
      setShowForm(false);
      fetchData();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async orderId => {
    try {
      await api.delete(`/orders/${orderId}`);
      toast.success("Pedido eliminado");
      setDeletingOrder(null);
      fetchData();
    } catch (err) {
      toastError(err);
    }
  };

  const checkoutFieldsWithIndexes = (form.checkoutFields || []).map((field, index) => ({
    field,
    index,
  }));
  const deliveryLocationField = checkoutFieldsWithIndexes.find(({ field }) =>
    isDeliveryLocationField(field)
  )?.field;
  const visibleCheckoutFields = checkoutFieldsWithIndexes.filter(
    ({ field }) => !isDeliveryLocationField(field)
  );
  const proofUrl = proofRemoved ? "" : form.paymentProofUrl || "";
  const proofName = proofFile?.name || (!proofRemoved ? form.paymentProofName : "") || "";
  const proofHref = buildFileHref(proofUrl);
  const proofPreviewUrl = proofFilePreviewUrl || proofUrl;
  const proofPreviewHref = proofFilePreviewUrl || proofHref;
  const proofFileInfo = {
    mimeType: proofFile?.type || form.paymentProofMimeType || "",
    name: proofName,
    url: proofPreviewUrl,
  };
  const proofIsImage = isImageFile(proofFileInfo);
  const proofIsPdf = isPdfFile(proofFileInfo);

  return (
    <>
      <Dialog open={modalOpen} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>Pedidos</DialogTitle>
        <DialogContent dividers>
          <div className={classes.headerActions}>
            <Typography
              variant="body2"
              color="textSecondary"
              className={privacyCensorSettings.contactNames ? classes.censoredText : undefined}
            >
              {privacyCensorSettings.contactNames
                ? "Contacto privado"
                : `${ticket?.contact?.name || ""} ${
                    ticket?.contact?.number ? `- ${ticket.contact.number}` : ""
                  }`}
            </Typography>
            <Button
              color="primary"
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                if (showForm) {
                  setShowForm(false);
                } else {
                  resetForm();
                  setShowForm(true);
                }
              }}
            >
              {showForm ? "Cancelar" : "Agregar"}
            </Button>
          </div>

          {showForm && (
            <Paper className={classes.formBox} elevation={0}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Contacto"
                    value={
                      privacyCensorSettings.contactNames
                        ? "Contacto privado"
                        : `${ticket?.contact?.name || ""} - ${ticket?.contact?.number || ""}`
                    }
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl variant="outlined" margin="dense" fullWidth>
                    <InputLabel>Estado</InputLabel>
                    <Select
                      value={form.status}
                      label="Estado"
                      onChange={event => setForm({ ...form, status: event.target.value })}
                    >
                      {statuses.map(status => (
                        <MenuItem key={status.id} value={status.key}>
                          {status.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl variant="outlined" margin="dense" fullWidth>
                    <InputLabel>Estado de pago</InputLabel>
                    <Select
                      value={form.paymentStatus || "pending"}
                      label="Estado de pago"
                      onChange={event => setForm({ ...form, paymentStatus: event.target.value })}
                    >
                      {Object.entries(paymentStatuses).map(([key, label]) => (
                        <MenuItem key={key} value={key}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl variant="outlined" margin="dense" fullWidth>
                    <InputLabel>Metodo de pago</InputLabel>
                    <Select
                      value={form.paymentMethodId || NO_PAYMENT_METHOD}
                      label="Metodo de pago"
                      onChange={event => {
                        if (event.target.value === "__create__") {
                          openCreatePaymentMethod();
                          return;
                        }
                        setForm({ ...form, paymentMethodId: event.target.value });
                      }}
                    >
                      <MenuItem value={NO_PAYMENT_METHOD}>Sin metodo</MenuItem>
                      <MenuItem value="__create__">Crear metodo...</MenuItem>
                      {paymentMethods.map(method => (
                        <MenuItem key={method.id} value={method.id}>
                          {method.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <div className={classes.paymentMethodActions}>
                    <Button
                      color="primary"
                      disabled={!form.paymentMethodId || form.paymentMethodId === NO_PAYMENT_METHOD}
                      onClick={() => openEditPaymentMethod(form.paymentMethodId)}
                    >
                      Editar metodo
                    </Button>
                  </div>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl variant="outlined" margin="dense" fullWidth>
                    <InputLabel>Metodo de envio</InputLabel>
                    <Select
                      value={form.shippingMethodId || NO_SHIPPING_METHOD}
                      label="Metodo de envio"
                      onChange={event => {
                        if (event.target.value === "__create__") {
                          openCreateShippingMethod();
                          return;
                        }

                        const method = shippingMethods.find(
                          entry => String(entry.id) === String(event.target.value)
                        );
                        setForm({
                          ...form,
                          shippingMethodId: event.target.value,
                          shipping:
                            event.target.value === NO_SHIPPING_METHOD
                              ? 0
                              : Number(method?.cost || 0),
                        });
                      }}
                    >
                      <MenuItem value={NO_SHIPPING_METHOD}>Sin envio</MenuItem>
                      <MenuItem value="__create__">Crear metodo...</MenuItem>
                      {shippingMethods.map(method => (
                        <MenuItem key={method.id} value={method.id}>
                          {method.name}
                          {method.cost ? ` - ${money(method.cost, orderSettings)}` : ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <div className={classes.paymentMethodActions}>
                    <Button
                      color="primary"
                      disabled={!form.shippingMethodId || form.shippingMethodId === NO_SHIPPING_METHOD}
                      onClick={() => openEditShippingMethod(form.shippingMethodId)}
                    >
                      Editar envio
                    </Button>
                  </div>
                </Grid>
              </Grid>

              <Typography variant="subtitle2">Productos</Typography>
              {(form.items || []).map((item, index) => (
                <div className={classes.itemRow} key={index}>
                  <IconButton
                    className={classes.itemDeleteButton}
                    size="small"
                    onClick={() => setForm({ ...form, items: form.items.filter((_, itemIndex) => itemIndex !== index) })}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                  <FormControl className={classes.itemCatalogWideField} variant="outlined" margin="dense">
                    <InputLabel>Producto</InputLabel>
                    <Select
                      value={item.catalogProductId || NO_CATALOG_PRODUCT}
                      label="Producto"
                      onChange={event => {
                        if (event.target.value === NO_CATALOG_PRODUCT) {
                          updateItem(index, {
                            catalogProductId: NO_CATALOG_PRODUCT,
                            productName: "",
                            catalogOptions: [],
                            optionSelections: {},
                            basePrice: 0,
                            variantName: NO_PRODUCT_VARIANT,
                            unitPrice: 0,
                          });
                          return;
                        }
                        const product = products.find(entry => String(entry.id) === String(event.target.value));
                        const nextItem = {
                          ...item,
                          catalogProductId: product?.id || NO_CATALOG_PRODUCT,
                          productName: product?.name || item.productName || "",
                          catalogOptions: product?.options || [],
                          optionSelections: {},
                          basePrice: Number(product?.price || 0),
                          variantName: NO_PRODUCT_VARIANT,
                        };
                        nextItem.unitPrice = calculateItemUnitPrice(nextItem);
                        updateItem(index, {
                          catalogProductId: nextItem.catalogProductId,
                          productName: nextItem.productName,
                          catalogOptions: nextItem.catalogOptions,
                          optionSelections: nextItem.optionSelections,
                          basePrice: nextItem.basePrice,
                          variantName: nextItem.variantName,
                          unitPrice: nextItem.unitPrice,
                        });
                      }}
                    >
                      <MenuItem value={NO_CATALOG_PRODUCT}>Producto manual</MenuItem>
                      {products.map(product => (
                        <MenuItem key={product.id} value={product.id}>
                          {product.name}
                          {product.price ? ` - ${money(product.price, orderSettings)}` : ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    className={classes.itemQuantityField}
                    label="Cant."
                    type="number"
                    value={item.quantity}
                    onChange={event => updateItem(index, { quantity: event.target.value })}
                    variant="outlined"
                    margin="dense"
                  />
                  <TextField
                    className={classes.itemPriceField}
                    label="Unitario"
                    type="number"
                    value={item.unitPrice}
                    onChange={event =>
                      updateItem(index, {
                        unitPrice: event.target.value,
                        basePrice:
                          item.catalogProductId && item.catalogProductId !== NO_CATALOG_PRODUCT
                            ? event.target.value
                            : item.basePrice,
                      })
                    }
                    variant="outlined"
                    margin="dense"
                    disabled={item.catalogProductId && item.catalogProductId !== NO_CATALOG_PRODUCT}
                  />
                  <div className={classes.itemTotalBox}>
                    <Typography variant="caption" color="textSecondary">
                      Total
                    </Typography>
                    <Typography variant="subtitle2">
                      {money(Number(item.quantity || 0) * Number(item.unitPrice || 0), orderSettings)}
                    </Typography>
                  </div>
                  {(!item.catalogProductId || item.catalogProductId === NO_CATALOG_PRODUCT) && (
                    <div className={classes.itemOptionsGrid}>
                      <TextField
                        className={classes.itemOptionControl}
                        label="Nombre manual"
                        value={item.productName}
                        onChange={event =>
                          updateItem(index, {
                            productName: event.target.value,
                            catalogProductId: NO_CATALOG_PRODUCT,
                            catalogOptions: [],
                            optionSelections: {},
                          })
                        }
                        variant="outlined"
                        margin="dense"
                        fullWidth
                      />
                      <TextField
                        className={classes.itemOptionControl}
                        label="Detalle / opciones"
                        value={item.variantName === NO_PRODUCT_VARIANT ? "" : item.variantName || ""}
                        onChange={event => updateItem(index, { variantName: event.target.value })}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                      />
                    </div>
                  )}
                  {item.catalogProductId &&
                    item.catalogProductId !== NO_CATALOG_PRODUCT &&
                    Boolean((item.catalogOptions || []).length) && (
                      <div className={classes.itemOptionsGrid}>
                        {(item.catalogOptions || []).map(option => {
                          const selectedValue = item.optionSelections?.[option.name];
                          const optionExtra = getOptionExtra(option, selectedValue);

                          return (
                            <div key={option.name}>
                              <FormControl
                                className={classes.itemOptionControl}
                                variant="outlined"
                                margin="dense"
                                fullWidth
                              >
                                <InputLabel>{option.name}</InputLabel>
                                <Select
                                  multiple={option.multiple === true}
                                  value={
                                    option.multiple
                                      ? normalizeSelectionArray(selectedValue)
                                      : selectedValue || NO_PRODUCT_VARIANT
                                  }
                                  label={option.name}
                                  renderValue={selected =>
                                    Array.isArray(selected)
                                      ? selected.length
                                        ? selected.join(", ")
                                        : option.required
                                        ? "Selecciona"
                                        : `Sin ${option.name}`
                                      : selected === NO_PRODUCT_VARIANT
                                      ? option.required
                                        ? "Selecciona"
                                        : `Sin ${option.name}`
                                      : selected
                                  }
                                  onChange={event => {
                                    const nextSelections = {
                                      ...(item.optionSelections || {}),
                                      [option.name]:
                                        event.target.value === NO_PRODUCT_VARIANT
                                          ? ""
                                          : event.target.value,
                                    };
                                    const nextItem = {
                                      ...item,
                                      optionSelections: nextSelections,
                                    };
                                    const unitPrice = calculateItemUnitPrice(nextItem);
                                    updateItem(index, {
                                      optionSelections: nextSelections,
                                      unitPrice,
                                      variantName: buildItemOptionText(nextItem) || NO_PRODUCT_VARIANT,
                                    });
                                  }}
                                >
                                  {!option.required && !option.multiple && (
                                    <MenuItem value={NO_PRODUCT_VARIANT}>
                                      Sin {option.name}
                                    </MenuItem>
                                  )}
                                  {getOptionValues(option).map(value => (
                                    <MenuItem key={value.name} value={value.name}>
                                      {option.multiple && (
                                        <Checkbox
                                          checked={normalizeSelectionArray(selectedValue).includes(value.name)}
                                        />
                                      )}
                                      <ListItemText
                                        primary={value.name}
                                        secondary={
                                          Number(value.priceDelta || 0)
                                            ? `+ ${money(value.priceDelta, orderSettings)}`
                                            : ""
                                        }
                                      />
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <Typography
                                className={classes.itemOptionPrice}
                                variant="caption"
                                color="textSecondary"
                              >
                                {optionExtra ? `Extra: ${money(optionExtra, orderSettings)}` : "Sin extra"}
                              </Typography>
                            </div>
                          );
                        })}
                      </div>
                    )}
                </div>
              ))}
              <Button color="primary" startIcon={<Add />} onClick={addItem}>
                Agregar producto
              </Button>

              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField label="Descuento" type="number" value={form.discount} onChange={event => setForm({ ...form, discount: event.target.value })} variant="outlined" margin="dense" fullWidth />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField label="Impuesto" type="number" value={form.tax} onChange={event => setForm({ ...form, tax: event.target.value })} variant="outlined" margin="dense" fullWidth />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    label="Costo de envio"
                    type="number"
                    value={form.shipping}
                    onChange={event => setForm({ ...form, shipping: event.target.value })}
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    disabled={form.shippingMethodId && form.shippingMethodId !== NO_SHIPPING_METHOD}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="caption" color="textSecondary">Total</Typography>
                  <Typography variant="h6">{money(orderTotal, orderSettings)}</Typography>
                </Grid>
              </Grid>
              <Typography variant="subtitle2">Detalles de facturacion</Typography>
              {visibleCheckoutFields.map(({ field, index }) => (
                <div className={classes.checkoutRow} key={index}>
                  <TextField
                    label="Campo"
                    value={field.name}
                    onChange={event => {
                      const checkoutFields = [...(form.checkoutFields || [])];
                      checkoutFields[index] = { ...field, name: event.target.value };
                      setForm({ ...form, checkoutFields });
                    }}
                    variant="outlined"
                    margin="dense"
                  />
                  <TextField
                    label="Valor"
                    value={field.value || ""}
                    onChange={event => {
                      const checkoutFields = [...(form.checkoutFields || [])];
                      checkoutFields[index] = { ...field, value: event.target.value };
                      setForm({ ...form, checkoutFields });
                    }}
                    variant="outlined"
                    margin="dense"
                    multiline
                    minRows={2}
                    fullWidth
                  />
                  <IconButton
                    size="small"
                    onClick={() =>
                      setForm({
                        ...form,
                        checkoutFields: form.checkoutFields.filter(
                          (_, fieldIndex) => fieldIndex !== index
                        ),
                      })
                    }
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </div>
              ))}
              <Button color="primary" startIcon={<Add />} onClick={addCheckoutField}>
                Nuevo campo
              </Button>

              <div className={classes.proofGrid}>
                <div className={classes.proofPanel}>
                  <Typography variant="subtitle2">Ubicacion de entrega</Typography>
                  <div className={classes.locationBox}>
                    {deliveryLocationField?.value ? (
                      <LocationPreview
                        link={deliveryLocationField.value}
                        description={deliveryLocationField.value}
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Sin ubicacion
                      </Typography>
                    )}
                  </div>
                </div>
                <div className={classes.proofPanel}>
                  <Typography variant="subtitle2">Comprobante</Typography>
                  <div className={classes.proofBox}>
                    <div className={classes.proofContent}>
                      <div>
                        <Typography variant="body2">Comprobante de pago</Typography>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          className={classes.proofName}
                        >
                          {proofName || "Sin comprobante"}
                        </Typography>
                      </div>
                      <div className={classes.proofActions}>
                        <Button component="label" variant="outlined" size="small">
                          {proofName ? "Reemplazar" : "Adjuntar"}
                          <input
                            type="file"
                            hidden
                            accept="image/*,.pdf"
                            onChange={event => {
                              const file = event.target.files?.[0] || null;
                              setProofFile(file);
                              if (file) setProofRemoved(false);
                            }}
                          />
                        </Button>
                        {proofHref && (
                          <Button
                            variant="outlined"
                            size="small"
                            href={proofHref}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Ver
                          </Button>
                        )}
                        {proofName && (
                          <Button
                            variant="outlined"
                            size="small"
                            color="secondary"
                            onClick={() => {
                              setProofFile(null);
                              setProofRemoved(Boolean(form.paymentProofUrl));
                            }}
                          >
                            Eliminar
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className={classes.proofPreviewBox}>
                      {proofPreviewUrl && proofIsImage ? (
                        proofFilePreviewUrl ? (
                          <img
                            src={proofFilePreviewUrl}
                            alt="Comprobante"
                            className={classes.proofPreview}
                          />
                        ) : (
                          <ModalImageCors
                            imageUrl={proofPreviewUrl}
                            className={classes.proofPreview}
                          />
                        )
                      ) : proofPreviewHref && proofIsPdf ? (
                        <iframe
                          title={proofName || "Comprobante PDF"}
                          className={classes.proofPdfPreview}
                          src={proofPreviewHref}
                        />
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          {proofName || "Sin archivo adjunto"}
                        </Typography>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <TextField
                label="Notas del cliente"
                value={form.customerNotes || ""}
                onChange={event => setForm({ ...form, customerNotes: event.target.value })}
                variant="outlined"
                margin="dense"
                multiline
                minRows={2}
                fullWidth
              />
              <TextField
                label="Notas internas"
                value={form.internalNotes || ""}
                onChange={event => setForm({ ...form, internalNotes: event.target.value })}
                variant="outlined"
                margin="dense"
                multiline
                minRows={2}
                fullWidth
              />
              <DialogActions>
                <Button onClick={() => setShowForm(false)} color="secondary" variant="outlined" disabled={saving}>
                  Cancelar
                </Button>
                <Button onClick={handleSave} color="primary" variant="contained" disabled={saving}>
                  {saving ? <CircularProgress size={20} /> : "Guardar"}
                </Button>
              </DialogActions>
            </Paper>
          )}

          {loading ? (
            <CircularProgress size={24} />
          ) : orders.length ? (
            orders.map(order => {
              const orderProofHref = buildFileHref(order.paymentProofUrl || "");
              const orderProofInfo = {
                mimeType: order.paymentProofMimeType || "",
                name: order.paymentProofName || "",
                url: order.paymentProofUrl || "",
              };
              const orderProofIsImage = isImageFile(orderProofInfo);
              const orderProofIsPdf = isPdfFile(orderProofInfo);

              return (
              <Paper className={classes.orderRow} elevation={0} key={order.id}>
                <div className={classes.rowHeader}>
                  <div className={classes.orderLegacyHeader}>
                    <Typography variant="subtitle1" className={classes.rowTitle}>
                      Pedido {order.number}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {order.items?.map(item => `${item.quantity}x ${item.productName}`).join(", ")}
                    </Typography>
                    {order.checkoutFields?.length ? (
                      <Typography variant="caption" color="textSecondary">
                        {order.checkoutFields.map(field => `${field.name}: ${field.value}`).join(" · ")}
                      </Typography>
                    ) : null}
                    <Typography variant="caption" color="textSecondary">
                      Pago: {paymentStatuses[order.paymentStatus] || order.paymentStatus}
                    </Typography>
                  </div>
                  <div className={classes.rowActions}>
                    <Chip
                      size="small"
                      label={statuses.find(status => status.key === order.status)?.name || order.status || "-"}
                      color={order.status === "completed" || order.status === "delivered" ? "primary" : "default"}
                    />
                    <IconButton size="small" onClick={() => handleEdit(order)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => setDeletingOrder(order)}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </div>
                </div>
                <Grid container spacing={1} className={classes.orderValues}>
                  {[
                    {
                      label: "Cliente",
                      value: ticket?.contact?.name || order.customerName || "Cliente sin nombre",
                    },
                    {
                      label: "Productos",
                      value: (order.items || [])
                        .map(item => `${item.quantity}x ${item.productName}`)
                        .join(", "),
                    },
                    { label: "Subtotal", value: money(order.subtotal, orderSettings) },
                    { label: "Envio", value: money(order.shipping, orderSettings) },
                    { label: "Total", value: money(order.total, orderSettings) },
                    {
                      label: "Metodo de pago",
                      value:
                        order.paymentMethod?.name ||
                        paymentMethods.find(method => Number(method.id) === Number(order.paymentMethodId))?.name ||
                        "Sin metodo",
                    },
                    {
                      label: "Estado de pago",
                      value: paymentStatuses[order.paymentStatus] || order.paymentStatus || "-",
                    },
                    {
                      label: "Metodo de envio",
                      value:
                        order.shippingMethod?.name ||
                        shippingMethods.find(method => Number(method.id) === Number(order.shippingMethodId))?.name ||
                        "Sin envio",
                    },
                    ...(order.checkoutFields || [])
                      .filter(field => !isDeliveryLocationField(field))
                      .map(field => ({
                        label: field.name,
                        value: field.value || "-",
                      })),
                  ].map(field => (
                    <Grid item xs={12} md={4} key={`${order.id}-${field.label}`}>
                      <div className={classes.orderValueItem}>
                        <Typography variant="caption" className={classes.muted}>
                          {field.label}
                        </Typography>
                        <Typography variant="body2">{field.value || "-"}</Typography>
                      </div>
                    </Grid>
                  ))}
                </Grid>
                {(order.checkoutFields || []).some(field => isDeliveryLocationField(field) && field.value) ||
                order.paymentProofUrl ? (
                  <div className={classes.orderMediaGrid}>
                    <div className={classes.orderMediaPanel}>
                      <Typography variant="subtitle2">Ubicacion de entrega</Typography>
                      <div className={classes.orderLocationBox}>
                        {(order.checkoutFields || []).find(field => isDeliveryLocationField(field))?.value ? (
                          <LocationPreview
                            link={(order.checkoutFields || []).find(field => isDeliveryLocationField(field))?.value}
                            description={(order.checkoutFields || []).find(field => isDeliveryLocationField(field))?.value}
                          />
                        ) : (
                          <Typography variant="body2" className={classes.muted}>
                            Sin ubicacion
                          </Typography>
                        )}
                      </div>
                    </div>
                    <div className={classes.orderMediaPanel}>
                      <Typography variant="subtitle2">Comprobante</Typography>
                      <div className={classes.orderProofBox}>
                        <div className={classes.orderFileCardHeader}>
                          <div>
                            <Typography variant="body2">Comprobante de pago</Typography>
                            <Typography variant="caption" className={classes.muted}>
                              {order.paymentProofName || "Sin comprobante"}
                            </Typography>
                          </div>
                          {order.paymentProofUrl && (
                            <div className={classes.orderFileActions}>
                              <Button
                                variant="outlined"
                                size="small"
                                href={orderProofHref}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Ver
                              </Button>
                            </div>
                          )}
                        </div>
                        <div className={classes.orderProofPreviewBox}>
                          {order.paymentProofUrl && orderProofIsImage ? (
                            <ModalImageCors
                              imageUrl={order.paymentProofUrl}
                              className={classes.orderProofPreview}
                            />
                          ) : orderProofHref && orderProofIsPdf ? (
                            <iframe
                              title={order.paymentProofName || "Comprobante PDF"}
                              className={classes.orderProofPdfPreview}
                              src={orderProofHref}
                            />
                          ) : (
                            <Typography variant="body2" className={classes.muted}>
                              {order.paymentProofName || "Sin archivo adjunto"}
                            </Typography>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </Paper>
              );
            })
          ) : (
            <Typography variant="body2" className={classes.emptyText}>
              Este ticket no tiene pedidos.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary" variant="outlined">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={methodModalOpen} onClose={closeMethodModal} maxWidth="sm" fullWidth>
        <DialogTitle>{methodForm.id ? "Editar metodo" : "Nuevo metodo de pago"}</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Nombre"
            value={methodForm.name}
            onChange={event => setMethodForm({ ...methodForm, name: event.target.value })}
            variant="outlined"
            margin="dense"
            fullWidth
          />
          <TextField
            label="Descripcion"
            value={methodForm.description}
            onChange={event => setMethodForm({ ...methodForm, description: event.target.value })}
            variant="outlined"
            margin="dense"
            fullWidth
          />
          <TextField
            label="Instrucciones"
            value={methodForm.instructions}
            onChange={event => setMethodForm({ ...methodForm, instructions: event.target.value })}
            variant="outlined"
            margin="dense"
            multiline
            minRows={3}
            fullWidth
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={methodForm.requiresProof}
                onChange={event =>
                  setMethodForm({ ...methodForm, requiresProof: event.target.checked })
                }
              />
            }
            label="Requiere comprobante"
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={methodForm.isActive !== false}
                onChange={event =>
                  setMethodForm({ ...methodForm, isActive: event.target.checked })
                }
              />
            }
            label="Activo"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMethodModal} color="secondary" variant="outlined">
            Cancelar
          </Button>
          <Button onClick={savePaymentMethod} color="primary" variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={shippingMethodModalOpen} onClose={closeShippingMethodModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {shippingMethodForm.id ? "Editar metodo de envio" : "Nuevo metodo de envio"}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Nombre"
            value={shippingMethodForm.name}
            onChange={event =>
              setShippingMethodForm({ ...shippingMethodForm, name: event.target.value })
            }
            variant="outlined"
            margin="dense"
            fullWidth
          />
          <TextField
            label="Descripcion"
            value={shippingMethodForm.description}
            onChange={event =>
              setShippingMethodForm({
                ...shippingMethodForm,
                description: event.target.value,
              })
            }
            variant="outlined"
            margin="dense"
            multiline
            minRows={2}
            fullWidth
          />
          <TextField
            label="Costo"
            type="number"
            value={shippingMethodForm.cost}
            onChange={event =>
              setShippingMethodForm({ ...shippingMethodForm, cost: event.target.value })
            }
            variant="outlined"
            margin="dense"
            fullWidth
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={shippingMethodForm.requiresLocation === true}
                onChange={event =>
                  setShippingMethodForm({
                    ...shippingMethodForm,
                    requiresLocation: event.target.checked,
                  })
                }
              />
            }
            label="Solicitar ubicacion de Maps"
          />
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={shippingMethodForm.isActive !== false}
                onChange={event =>
                  setShippingMethodForm({
                    ...shippingMethodForm,
                    isActive: event.target.checked,
                  })
                }
              />
            }
            label="Activo"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeShippingMethodModal} color="secondary" variant="outlined">
            Cancelar
          </Button>
          <Button onClick={saveShippingMethod} color="primary" variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title="Eliminar pedido"
        open={!!deletingOrder}
        onClose={() => setDeletingOrder(null)}
        onConfirm={() => handleDelete(deletingOrder.id)}
      >
        Esta accion no se puede deshacer.
      </ConfirmationModal>
    </>
  );
};

export default TicketOrdersModal;
